/* =====================================================================
   leadyourday – Core JS (MG Commerce)
   Custom Elements initialisieren sich selbst, auch nach Theme-Editor-Reloads.
   ===================================================================== */
(() => {
  if (window.__lydCore) return;
  window.__lydCore = true;
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = () => !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Reveal + Zähler ---------- */
  const countUp = (el) => {
    if (el.dataset.lydDone) return;
    el.dataset.lydDone = '1';
    const to = parseFloat(el.dataset.lydCount || '0');
    if (reduce() || !to) { el.textContent = String(to); return; }
    const t0 = performance.now(), d = 1400;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / d);
      el.textContent = String(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  /* Gestaffeltes Einblenden: Elemente, die im selben Moment sichtbar werden und denselben Eltern-Container
     haben (Produktkarten, Vergleichs-/Bento-Kacheln …), laufen in Lesereihenfolge nacheinander ein –
     Reihe 1 von links nach rechts, dann Reihe 2. Einzelne Elemente behalten ihre d1–d5-Verzögerung. */
  const STAGGER = 0.09, STAGGER_MAX = 1.2;
  const stagger = (entries) => {
    const groups = new Map();
    entries.forEach((e) => {
      if (!e.isIntersecting || !e.target.classList.contains('lyd-rv')) return;
      const p = e.target.parentElement;
      if (!groups.has(p)) groups.set(p, []);
      groups.get(p).push(e.target);
    });
    groups.forEach((els) => {
      if (els.length < 2 && !els[0].hasAttribute('data-lyd-card')) return;
      els.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
      els.forEach((el, i) => { el.style.transitionDelay = Math.min(i * STAGGER, STAGGER_MAX).toFixed(2) + 's'; });
    });
  };
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    stagger(entries);
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      if (e.target.classList.contains('lyd-rv')) {
        /* nach dem Einblenden Verzögerung aufheben, damit Hover-Effekte sofort reagieren */
        const d = (parseFloat(getComputedStyle(e.target).transitionDelay) || 0) * 1000;
        setTimeout(() => { e.target.style.transitionDelay = '0s'; }, d + 1000);
      }
      e.target.querySelectorAll('[data-lyd-count]').forEach(countUp);
      if (e.target.matches('[data-lyd-count]')) countUp(e.target);
      io.unobserve(e.target);
    });
  }, { threshold: 0.15 }) : null;
  const scan = (root = document) => {
    root.querySelectorAll('.lyd-rv:not(.is-in), [data-lyd-count]:not([data-lyd-done]), [data-lyd-observe]:not(.is-in)').forEach((el) => {
      if (!io || inEditor()) { el.classList.add('is-in'); el.querySelectorAll && el.querySelectorAll('[data-lyd-count]').forEach(countUp); if (el.matches('[data-lyd-count]')) countUp(el); return; }
      io.observe(el);
    });
  };
  document.addEventListener('DOMContentLoaded', () => scan());
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
  if (document.readyState !== 'loading') scan();

  /* ---------- Header: Mega-Menü, Drawer, Sticky, Warenkorb ---------- */
  class LydHeader extends HTMLElement {
    connectedCallback() {
      this.megaBtns = this.querySelectorAll('[data-lyd-mega-btn]');
      this.megaBtns.forEach((btn) => {
        const panel = this.querySelector('#' + btn.getAttribute('aria-controls'));
        if (!panel) return;
        const set = (o) => { btn.setAttribute('aria-expanded', String(o)); panel.hidden = !o; this.classList.toggle('is-open', o); };
        btn.addEventListener('click', () => set(btn.getAttribute('aria-expanded') !== 'true'));
        if (this.dataset.megaHover === 'true') btn.addEventListener('mouseenter', () => set(true));
        this.addEventListener('mouseleave', () => set(false));
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') set(false); });
      });
      const drawer = this.querySelector('[data-lyd-drawer]');
      this.querySelectorAll('[data-lyd-drawer-open]').forEach((b) => b.addEventListener('click', () => { clearTimeout(this._dt); drawer.hidden = false; void drawer.offsetWidth; drawer.classList.add('is-open'); b.setAttribute('aria-expanded', 'true'); document.documentElement.style.overflow = 'hidden'; drawer.querySelector('button, a')?.focus({ preventScroll: true }); }));
      const close = () => { if (!drawer || drawer.hidden) return; drawer.classList.remove('is-open'); document.documentElement.style.overflow = ''; this.querySelector('[data-lyd-drawer-open]')?.setAttribute('aria-expanded', 'false'); clearTimeout(this._dt); this._dt = setTimeout(() => { drawer.hidden = true; }, reduce() ? 0 : 480); };
      this.querySelectorAll('[data-lyd-drawer-close]').forEach((b) => b.addEventListener('click', close));
      drawer?.addEventListener('click', (e) => { if (e.target === drawer) close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
      /* Warenkorb-Button: öffnet den Horizon-Cart-Drawer, sonst Link auf /cart */
      this.querySelectorAll('[data-lyd-cart-toggle]').forEach((b) => b.addEventListener('click', (e) => {
        const cd = document.getElementById('cart-drawer');
        if (cd && typeof cd.toggle === 'function') { e.preventDefault(); cd.toggle(); }
      }));
      /* Sticky */
      if (this.dataset.sticky === 'true') {
        const off = parseInt(this.dataset.stickyOffset, 10) || 80;
        const smart = this.dataset.stickyMode !== 'always';
        const host = this.parentElement;
        const overlay = this.classList.contains('is-overlay');
        let last = window.scrollY, ticking = false;
        const stick = (on) => {
          if (on === this.classList.contains('is-stuck')) return;
          if (on && !overlay && host) host.style.minHeight = this.offsetHeight + 'px';
          this.classList.toggle('is-stuck', on);
          if (!on) { this.classList.remove('is-hidden'); if (host) host.style.minHeight = ''; }
        };
        const update = () => {
          ticking = false;
          const y = window.scrollY, d = y - last;
          if (Math.abs(d) < 6 && y > off) return;
          last = y;
          if (y <= off) { stick(false); return; }
          if (!smart) { stick(true); return; }
          if (this.classList.contains('is-open')) return;
          if (d < 0) {
            if (!this.classList.contains('is-stuck')) { stick(true); this.classList.add('is-hidden'); void this.offsetWidth; }
            this.classList.remove('is-hidden');
          } else if (this.classList.contains('is-stuck')) {
            this.classList.add('is-hidden');
          }
        };
        window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
        update();
      }
    }
  }
  if (!customElements.get('lyd-header')) customElements.define('lyd-header', LydHeader);

  /* ---------- Hero: Sorten-Rotation ---------- */
  class LydHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-lyd-slide]')];
      this.dots = [...this.querySelectorAll('[data-lyd-dot]')];
      if (!this.slides.length) return;
      this.i = 0;
      this.dur = (parseFloat(this.dataset.interval) || 8) * 1000;
      this.style.setProperty('--lyd-hero-dur', this.dur + 'ms');
      this.dots.forEach((d) => d.addEventListener('click', () => { this.go(+d.dataset.lydDot); this.auto(); }));
      /* Pause nur beim Überfahren von Produktbild/Farbpunkten und nur mit echter Maus (kein Touch) */
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (this.dataset.pauseHover !== 'false' && canHover) {
        this.querySelectorAll('.lyd-hero__pwrap, .lyd-hero__sw').forEach((z) => {
          z.addEventListener('mouseenter', () => this.pause('hover'));
          z.addEventListener('mouseleave', () => this.resume('hover'));
        });
      }
      this._holds = new Set();
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pause('tab'); else this.resume('tab'); });
      let x0 = null;
      this.addEventListener('pointerdown', (e) => { x0 = e.clientX; });
      this.addEventListener('pointerup', (e) => { if (x0 === null) return; const dx = e.clientX - x0; x0 = null; if (Math.abs(dx) > 50) { this.go(this.i + (dx < 0 ? 1 : -1)); this.auto(); } });
      this.go(0, true); this.auto();
    }
    disconnectedCallback() { clearTimeout(this.t); }
    pause(why) {
      if (!this._holds) return; this._holds.add(why);
      if (this._holds.size > 1 || !this._start) return;
      clearTimeout(this.t); this._left = Math.max(0, this._left - (performance.now() - this._start)); this._start = 0;
      this.classList.add('is-paused');
    }
    resume(why) {
      if (!this._holds || !this._holds.delete(why) || this._holds.size) return;
      this.classList.remove('is-paused');
      if (this.dataset.autoplay === 'true' && !reduce() && this.slides.length > 1) this.run(this._left || this.dur);
    }
    run(ms) { clearTimeout(this.t); this._left = ms; this._start = performance.now(); this.t = setTimeout(() => { this.go(this.i + 1); this.auto(); }, ms); }
    go(j, first) {
      const n = this.slides.length; this.i = (j + n) % n;
      const s = this.slides[this.i];
      this.slides.forEach((el, x) => { el.classList.toggle('is-on', x === this.i); el.setAttribute('aria-hidden', String(x !== this.i)); });
      this.querySelectorAll('[data-lyd-slide-img]').forEach((el) => el.classList.toggle('is-on', +el.dataset.lydSlideImg === this.i));
      this.dots.forEach((d, x) => {
        d.setAttribute('aria-pressed', String(x === this.i));
        const c = d.querySelector('circle'); if (c) { c.style.animation = 'none'; void c.getBoundingClientRect(); c.style.animation = ''; }
      });
      const bg = s.dataset.bg; if (bg && this.dataset.tint !== 'false') this.style.setProperty('--lyd-hero-bg', bg);
      const ghost = this.querySelector('[data-lyd-ghost]');
      const upd = () => { if (ghost) ghost.textContent = s.dataset.name || ''; };
      if (first || reduce()) { upd(); return; }
      ghost?.classList.add('is-out'); setTimeout(() => { upd(); ghost?.classList.remove('is-out'); }, 450);
    }
    auto() {
      clearTimeout(this.t); this._start = 0;
      if (this.dataset.autoplay !== 'true' || reduce() || this.slides.length < 2) return;
      if (this._holds && this._holds.size) { this._left = this.dur; return; }
      this.run(this.dur);
    }
  }
  if (!customElements.get('lyd-hero')) customElements.define('lyd-hero', LydHero);

  /* ---------- Produkt-Raster: Filter + Tilt ---------- */
  class LydGrid extends HTMLElement {
    connectedCallback() {
      const tabs = this.querySelectorAll('[data-lyd-filter]');
      const cards = [...this.querySelectorAll('[data-lyd-card]')];
      tabs.forEach((t) => t.addEventListener('click', () => {
        const v = t.dataset.lydFilter;
        tabs.forEach((x) => x.setAttribute('aria-pressed', String(x === t)));
        let n = 0;
        cards.forEach((c) => {
          const tags = (c.dataset.moments || '').split(',').map((s) => s.trim().toLowerCase());
          const show = v === 'all' || tags.includes(v.toLowerCase());
          c.hidden = !show;
          if (show && !reduce() && c.animate) c.animate([{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'none' }], { duration: 450, delay: (n++) * 50, fill: 'backwards' });
        });
        const empty = this.querySelector('[data-lyd-empty]'); if (empty) empty.hidden = cards.some((c) => !c.hidden);
      }));
      if (this.dataset.tilt === 'true' && !reduce()) {
        this.addEventListener('pointermove', (e) => {
          if (e.pointerType !== 'mouse') return;
          const c = e.target.closest('.lyd-card'); if (!c) return;
          const r = c.getBoundingClientRect(), x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
          c.style.transform = `perspective(800px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) translateY(-4px)`;
        });
        this.addEventListener('pointerout', (e) => { const c = e.target.closest('.lyd-card'); if (c && !c.contains(e.relatedTarget)) c.style.transform = ''; });
      }
    }
  }
  if (!customElements.get('lyd-grid')) customElements.define('lyd-grid', LydGrid);

  /* ---------- Spotlight: Vorteile nacheinander hervorheben ---------- */
  class LydCycle extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-lyd-cycle-item]')];
      const ms = (parseFloat(this.dataset.interval) || 2.6) * 1000;
      if (!this.items.length || this.dataset.enabled !== 'true' || reduce()) return;
      let i = 0;
      this.t = setInterval(() => { this.items.forEach((el, x) => el.classList.toggle('is-act', x === i)); i = (i + 1) % this.items.length; }, ms);
    }
    disconnectedCallback() { clearInterval(this.t); }
  }
  if (!customElements.get('lyd-cycle')) customElements.define('lyd-cycle', LydCycle);

  /* ---------- Ritual: Bildwechsel pro Schritt ---------- */
  class LydRitual extends HTMLElement {
    connectedCallback() {
      this.frames = [...this.querySelectorAll('[data-lyd-frame]')];
      this.steps = [...this.querySelectorAll('[data-lyd-step]')];
      this.cap = this.querySelector('[data-lyd-cap]');
      this.sbars = [...this.querySelectorAll('[data-lyd-sbar]')];
      if (!this.steps.length) return;
      this.ms = (parseFloat(this.dataset.interval) || 4.5) * 1000;
      this.style.setProperty('--lyd-rdur', this.ms + 'ms');
      this.steps.forEach((s, i) => {
        s.addEventListener('click', () => { this.set(i); this.auto(); });
        s.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.set(i); this.auto(); } });
      });
      this.set(0); this.auto();
    }
    disconnectedCallback() { clearInterval(this.t); }
    set(i) {
      this.i = i;
      this.frames.forEach((f, x) => f.classList.toggle('is-on', x === i));
      this.steps.forEach((s, x) => { s.classList.remove('is-act'); s.setAttribute('aria-current', String(x === i)); if (x === i) { void s.offsetWidth; s.classList.add('is-act'); } });
      if (this.cap) this.cap.textContent = this.steps[i]?.dataset.caption || '';
      this.sbars.forEach((b, x) => { b.classList.toggle('is-done', x < i); b.classList.remove('is-act'); if (x === i) { void b.offsetWidth; b.classList.add('is-act'); } });
    }
    auto() { clearInterval(this.t); if (this.dataset.autoplay === 'true' && !reduce()) this.t = setInterval(() => this.set((this.i + 1) % this.steps.length), this.ms); }
  }
  if (!customElements.get('lyd-ritual')) customElements.define('lyd-ritual', LydRitual);

  /* ---------- Quiz ---------- */
  class LydQuiz extends HTMLElement {
    connectedCallback() {
      this.qs = [...this.querySelectorAll('[data-lyd-q]')];
      this.results = [...this.querySelectorAll('[data-lyd-result]')];
      this.prog = [...this.querySelectorAll('[data-lyd-prog]')];
      this.done = this.querySelector('[data-lyd-done]');
      this.prio = (this.dataset.priority || '2,1,3').split(',').map((n) => parseInt(n, 10) - 1);
      this.answers = [];
      this.qs.forEach((q, qi) => q.querySelectorAll('[data-lyd-answer]').forEach((a) => a.addEventListener('click', () => {
        q.querySelectorAll('[data-lyd-answer]').forEach((x) => x.setAttribute('aria-pressed', String(x === a)));
        this.answers[qi] = a.dataset.product || '';
        setTimeout(() => this.next(qi), 420);
      })));
      this.querySelector('[data-lyd-restart]')?.addEventListener('click', () => { this.answers = []; this.show(0, true); this.showResult(this.dataset.default, false); });
      this.show(0, false);
      this.showResult(this.dataset.default, false);
    }
    pick() { for (const i of this.prio) { if (this.answers[i]) return this.answers[i]; } return this.answers.find(Boolean) || this.dataset.default; }
    next(qi) {
      const h = this.pick();
      if (qi < this.qs.length - 1) { this.show(qi + 1, true); this.showResult(h, false); }
      else { this.swap(this.done, true); this.showResult(h, true); this.prog.forEach((p) => p.classList.add('is-on')); }
    }
    show(i, animate) {
      this.swap(this.qs[i], animate, () => this.qs[i].querySelectorAll('[data-lyd-answer]').forEach((a) => a.setAttribute('aria-pressed', 'false')));
      this.prog.forEach((p, x) => p.classList.toggle('is-on', x <= i));
    }
    swap(target, animate, before) {
      if (!target) return;
      const panels = [...this.qs, this.done].filter(Boolean);
      const cur = panels.find((p) => !p.hidden && p !== target);
      const enter = () => {
        panels.forEach((p) => { if (p !== target) { p.hidden = true; p.classList.remove('is-out'); } });
        if (before) before();
        if (!animate || reduce()) { target.hidden = false; return; }
        target.classList.add('is-pre'); target.hidden = false;
        void target.offsetWidth; target.classList.remove('is-pre');
      };
      if (animate && cur && !reduce()) { cur.classList.add('is-out'); clearTimeout(this._t); this._t = setTimeout(enter, 320); } else enter();
    }
    showResult(handle, final) {
      let hit = false;
      this.results.forEach((r) => { const on = r.dataset.lydResult === handle && !hit; if (on) hit = true; if (on && r.hidden) { r.classList.remove('is-show'); void r.offsetWidth; r.classList.add('is-show'); } r.hidden = !on; r.classList.toggle('is-final', !!final); });
      if (!hit && this.results[0]) { this.results[0].hidden = false; this.results[0].classList.toggle('is-final', !!final); }
    }
  }
  if (!customElements.get('lyd-quiz')) customElements.define('lyd-quiz', LydQuiz);

  /* ---------- Akkordeon: weiches Auf-/Zuklappen (MG 2026-09-24) ---------- */
  if (!window.__lydSmoothDetails) {
    window.__lydSmoothDetails = true;
    const SEL = '.lyd-sec details, .lyd-cseo details';
    document.addEventListener('click', (e) => {
      const sum = e.target.closest('summary');
      const el = sum && sum.parentElement;
      if (!el || el.tagName !== 'DETAILS' || !el.matches(SEL) || sum !== el.querySelector('summary')) return;
      if (reduce() || !el.animate) return;
      e.preventDefault();
      const start = el.offsetHeight;
      if (el._lydAnim) { const old = el._lydAnim; el._lydAnim = null; old.cancel(); }
      const body = [...el.children].filter((c) => c !== sum);
      const opening = !el.open || el.classList.contains('is-closing');
      el.style.overflow = 'hidden';
      if (opening) { el.classList.remove('is-closing'); el.open = true; }
      const end = opening ? el.offsetHeight : sum.offsetHeight;
      if (!opening) el.classList.add('is-closing');
      const anim = el.animate({ height: [start + 'px', end + 'px'] }, { duration: opening ? 420 : 340, easing: 'cubic-bezier(.25,.8,.25,1)' });
      body.forEach((b) => b.animate({ opacity: opening ? [0, 1] : [1, 0], transform: opening ? ['translateY(-6px)', 'none'] : ['none', 'translateY(-6px)'] }, { duration: opening ? 420 : 240, easing: 'ease', fill: 'both' }));
      el._lydAnim = anim;
      const done = () => {
        if (el._lydAnim !== anim) return;
        el._lydAnim = null; clearTimeout(el._lydT); el.style.overflow = '';
        if (!opening) { el.open = false; el.classList.remove('is-closing'); }
        body.forEach((b) => b.getAnimations().forEach((x) => x.cancel()));
      };
      anim.onfinish = done;
      clearTimeout(el._lydT); el._lydT = setTimeout(done, (opening ? 420 : 340) + 120);
    });
  }

  /* ======================= PRODUKTSEITE ======================= */
  const fmtMoney = (cents, cur) => { try { return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: cur || 'EUR' }).format(cents / 100); } catch (e) { return (cents / 100).toFixed(2).replace('.', ',') + ' €'; } };

  /* ---------- Kaufbox: Menge, Staffel, Preis, Grundpreis, Tassenpreis, Galerie ---------- */
  class LydBuybox extends HTMLElement {
    connectedCallback() {
      this.d = this.dataset;
      this.price = parseInt(this.d.price, 10) || 0;
      this.compare = parseInt(this.d.compare, 10) || 0;
      this.grams = parseFloat(this.d.grams) || 180;
      this.cups = parseInt(this.d.cups, 10) || 12;
      this.cur = this.d.currency || 'EUR';
      this.tiers = [...this.querySelectorAll('[data-lyd-tier]')];
      this.qtyInput = this.querySelector('[data-lyd-qty]');
      this.addonCb = this.querySelector('[data-lyd-addon]');
      this.qty = 1;
      this.tiers.forEach((t) => t.addEventListener('click', () => this.setQty(parseInt(t.dataset.qty, 10))));
      /* Radiogroup per Tastatur (Pfeiltasten wechseln die Staffel) */
      this.tiers.forEach((t, i) => t.addEventListener('keydown', (e) => {
        const k = e.key; let j = -1;
        if (k === 'ArrowRight' || k === 'ArrowDown') j = (i + 1) % this.tiers.length;
        else if (k === 'ArrowLeft' || k === 'ArrowUp') j = (i - 1 + this.tiers.length) % this.tiers.length;
        else if (k === 'Home') j = 0; else if (k === 'End') j = this.tiers.length - 1;
        if (j < 0) return;
        e.preventDefault(); this.setQty(parseInt(this.tiers[j].dataset.qty, 10)); this.tiers[j].focus();
      }));
      this.querySelectorAll('[data-lyd-step]').forEach((b) => b.addEventListener('click', () => this.setQty(this.qty + parseInt(b.dataset.lydStep, 10))));
      this.qtyInput?.addEventListener('change', () => this.setQty(parseInt(this.qtyInput.value, 10) || 1));
      this.addonCb?.addEventListener('change', () => this.render());
      const vsel = this.querySelector('[data-lyd-variant]');
      vsel?.addEventListener('change', () => {
        const o = vsel.selectedOptions[0];
        this.price = parseInt(o.dataset.price, 10) || this.price; this.compare = parseInt(o.dataset.compare, 10) || 0;
        this.querySelectorAll('input[name="id"]').forEach((i) => { i.value = vsel.value; });
        const atc = this.querySelector('[data-lyd-main-atc]'); if (atc) atc.disabled = o.dataset.available !== 'true';
        this.render();
      });
      this.setQty(parseInt(this.qtyInput?.value, 10) || parseInt(this.d.defaultQty, 10) || 1);
    }
    /* Staffel: fester Rabatt je Box (data-off, Cent) der höchsten erreichten Stufe × Menge –
       identisch mit den automatischen Shopify-Rabatten (Theme-Einstellungen „leadyourday Staffel“). */
    saving(q) {
      let off = 0, s = 0;
      /* Karten (data-lyd-tier) + unsichtbare Zwischenstufen ohne Karte (data-lyd-step-tier, z. B. 2 Boxen) */
      [...this.tiers, ...this.querySelectorAll('[data-lyd-step-tier]')].forEach((t) => {
        const tq = parseInt(t.dataset.qty, 10);
        if (q < tq) return;
        if (t.dataset.off !== undefined) off = Math.max(off, parseInt(t.dataset.off, 10) || 0);
        else s = Math.max(s, parseInt(t.dataset.save, 10) || 0);
      });
      return off ? off * q : s;
    }
    setQty(q) {
      const max = parseInt(this.d.max, 10) || 99;
      this.qty = Math.min(max, Math.max(1, q || 1));
      if (this.qtyInput) this.qtyInput.value = this.qty;
      this.render();
    }
    render() {
      /* keine Streichpreise für die Staffel – „was“ nur bei echtem Vergleichspreis */
      const q = this.qty, sv = this.saving(q), total = this.price * q - sv, full = this.compare > this.price ? this.compare * q : 0;
      let act = null; this.tiers.forEach((t) => { if (q >= parseInt(t.dataset.qty, 10)) act = t; });
      this.tiers.forEach((t) => { const on = t === act; t.setAttribute('aria-checked', String(on)); t.classList.toggle('is-on', on); t.tabIndex = on ? 0 : -1; });
      const perKg = Math.round(total / ((this.grams * q) / 1000));
      const perCup = Math.round(total / (this.cups * q));
      this.querySelectorAll('[data-lyd-p="price"]').forEach((e) => { e.textContent = fmtMoney(total, this.cur); });
      this.querySelectorAll('[data-lyd-p="was"]').forEach((e) => { e.hidden = !(full > total); e.textContent = fmtMoney(full, this.cur); });
      this.querySelectorAll('[data-lyd-p="unit"]').forEach((e) => { e.textContent = fmtMoney(perKg, this.cur) + ' / kg'; });
      this.querySelectorAll('[data-lyd-p="cup"]').forEach((e) => { e.textContent = fmtMoney(perCup, this.cur); });
      this.querySelectorAll('[data-lyd-p="save"]').forEach((e) => { e.hidden = sv <= 0; const t = e.dataset.tpl || '[betrag]'; e.textContent = t.replace('[betrag]', fmtMoney(sv, this.cur)); });
      const addon = this.addonCb && this.addonCb.checked ? parseInt(this.addonCb.dataset.price, 10) || 0 : 0;
      this.querySelectorAll('[data-lyd-p="btn"]').forEach((e) => { e.textContent = fmtMoney(total + addon, this.cur); });
      document.querySelectorAll('[data-lyd-sticky-price]').forEach((e) => { e.textContent = fmtMoney(total, this.cur); });
      document.querySelectorAll('[data-lyd-sticky-unit]').forEach((e) => { e.textContent = fmtMoney(perKg, this.cur) + ' / kg'; });
    }
    getItems() {
      const id = this.querySelector('input[name="id"]')?.value;
      const items = [{ id, quantity: this.qty }];
      if (this.addonCb && this.addonCb.checked && this.addonCb.dataset.variantId) items.push({ id: this.addonCb.dataset.variantId, quantity: 1 });
      return items;
    }
  }
  if (!customElements.get('lyd-buybox')) customElements.define('lyd-buybox', LydBuybox);

  /* ---------- Produktgalerie: Endlos-Slider (Klone), Pfeile, Kacheln/Punkte, Wischen (Maus + Finger) ---------- */
  class LydGallery extends HTMLElement {
    connectedCallback() {
      this.n = parseInt(this.dataset.count, 10) || 0;
      this.track = this.querySelector('[data-lyd-gtrack]');
      this.vp = this.querySelector('[data-lyd-gvp]');
      this.frames = [...this.querySelectorAll('[data-lyd-gframe]')];
      if (!this.track || this.n < 2) return;
      this.pos = 1; /* 0 = Klon des letzten Bildes, 1..n = echte Bilder, n+1 = Klon des ersten */
      this.busy = false;
      this.go(1, false);
      this.querySelector('[data-lyd-gprev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-lyd-gnext]')?.addEventListener('click', () => this.step(1));
      this.querySelectorAll('[data-lyd-gdot]').forEach((b) => b.addEventListener('click', () => this.go(parseInt(b.dataset.lydGdot, 10) + 1, true)));
      this.track.addEventListener('transitionend', (e) => { if (e.target === this.track) this.settle(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'ArrowLeft') this.step(-1); else if (e.key === 'ArrowRight') this.step(1); });
      this.bindDrag();
    }
    reduce() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    go(pos, anim, dx = 0) {
      this.pos = pos;
      this.track.classList.toggle('is-anim', !!anim && !this.reduce());
      this.track.style.transform = `translate3d(calc(${-pos * 100}% + ${dx}px),0,0)`;
      clearTimeout(this.t);
      if (!anim || this.reduce()) this.settle(); else { this.busy = true; this.t = setTimeout(() => { if (this.busy) this.settle(); }, 650); }
      this.mark();
    }
    step(d) { if (this.busy) return; this.go(this.pos + d, true); }
    /* Nach der Animation auf einem Klon: ohne Übergang auf das echte Bild springen (unsichtbar, kein Zurückrollen) */
    settle() {
      this.busy = false;
      let p = this.pos;
      if (p === 0) p = this.n; else if (p === this.n + 1) p = 1;
      if (p !== this.pos) {
        this.track.classList.remove('is-anim');
        this.pos = p;
        this.track.style.transform = `translate3d(${-p * 100}%,0,0)`;
        void this.track.offsetWidth;
      }
    }
    mark() {
      const i = ((this.pos - 1) % this.n + this.n) % this.n;
      this.frames.forEach((f, x) => f.setAttribute('aria-hidden', String(x !== i)));
      this.querySelectorAll('[data-lyd-gdot]').forEach((b) => b.setAttribute('aria-current', String(parseInt(b.dataset.lydGdot, 10) === i)));
      const th = this.querySelector('.lyd-pm__thumbs [aria-current="true"]');
      const row = th && th.parentElement;
      if (row && row.scrollWidth > row.clientWidth) row.scrollTo({ left: th.offsetLeft - (row.clientWidth - th.offsetWidth) / 2, behavior: this.reduce() ? 'auto' : 'smooth' });
    }
    bindDrag() {
      const vp = this.vp; let x0 = 0, y0 = 0, dx = 0, on = false, axis = '';
      const w = () => vp.clientWidth || 1;
      vp.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || this.busy) return;
        on = true; axis = ''; dx = 0; x0 = e.clientX; y0 = e.clientY;
      });
      vp.addEventListener('pointermove', (e) => {
        if (!on) return;
        const mx = e.clientX - x0, my = e.clientY - y0;
        if (!axis) { if (Math.abs(mx) < 6 && Math.abs(my) < 6) return; axis = Math.abs(mx) > Math.abs(my) ? 'x' : 'y'; if (axis === 'x') { vp.setPointerCapture(e.pointerId); vp.classList.add('is-drag'); } }
        if (axis !== 'x') return;
        e.preventDefault(); dx = mx;
        this.track.classList.remove('is-anim');
        this.track.style.transform = `translate3d(calc(${-this.pos * 100}% + ${dx}px),0,0)`;
      });
      const end = () => {
        if (!on) return; on = false; vp.classList.remove('is-drag');
        if (axis !== 'x') return;
        const lim = Math.min(80, w() * 0.15);
        if (dx <= -lim) this.go(this.pos + 1, true); else if (dx >= lim) this.go(this.pos - 1, true); else this.go(this.pos, true);
      };
      vp.addEventListener('pointerup', end); vp.addEventListener('pointercancel', end); vp.addEventListener('lostpointercapture', end);
      vp.addEventListener('click', (e) => { if (Math.abs(dx) > 6) { e.preventDefault(); e.stopPropagation(); } }, true);
    }
  }
  if (!customElements.get('lyd-gallery')) customElements.define('lyd-gallery', LydGallery);

  /* ---------- Sticky-Kaufleiste ---------- */
  class LydSticky extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector('lyd-buybox') || document.querySelector(this.dataset.target || '');
      if (!target || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver((es) => es.forEach((e) => this.classList.toggle('is-show', !e.isIntersecting && e.boundingClientRect.top < 0)), { threshold: 0 });
      this.io.observe(target);
    }
    disconnectedCallback() { this.io?.disconnect(); }
  }
  if (!customElements.get('lyd-sticky')) customElements.define('lyd-sticky', LydSticky);

  /* ---------- Slider (Cross-Sell) ---------- */
  class LydSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lyd-track]');
      this.bar = this.querySelector('[data-lyd-progress]');
      if (!this.track) return;
      const step = () => { const c = this.track.firstElementChild; const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16; return c ? c.getBoundingClientRect().width + gap : 300; };
      this.querySelectorAll('[data-lyd-dir]').forEach((b) => b.addEventListener('click', () => this.track.scrollBy({ left: step() * parseInt(b.dataset.lydDir, 10), behavior: reduce() ? 'auto' : 'smooth' })));
      const prog = () => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.querySelectorAll('[data-lyd-dir="-1"]').forEach((b) => { b.disabled = this.track.scrollLeft <= 2; });
        this.querySelectorAll('[data-lyd-dir="1"]').forEach((b) => { b.disabled = this.track.scrollLeft >= max - 2; });
        if (!this.bar) return;
        const w = Math.max(12, (this.track.clientWidth / this.track.scrollWidth) * 100);
        this.bar.style.width = w + '%'; this.bar.style.left = (max > 0 ? (this.track.scrollLeft / max) * (100 - w) : 0) + '%';
      };
      this.track.addEventListener('scroll', prog, { passive: true }); window.addEventListener('resize', prog); prog();
    }
  }
  if (!customElements.get('lyd-slider')) customElements.define('lyd-slider', LydSlider);

  /* ---------- Hotspots (Aufbau) ---------- */
  class LydHotspots extends HTMLElement {
    connectedCallback() {
      const set = (i) => {
        this.querySelectorAll('[data-lyd-hs]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.lydHs === i)));
        this.querySelectorAll('[data-lyd-hi]').forEach((b) => b.classList.toggle('is-act', b.dataset.lydHi === i));
      };
      this.querySelectorAll('[data-lyd-hs],[data-lyd-hi]').forEach((b) => b.addEventListener('click', () => set(b.dataset.lydHs || b.dataset.lydHi)));
    }
  }
  if (!customElements.get('lyd-hotspots')) customElements.define('lyd-hotspots', LydHotspots);

  /* ---------- Umschalter (z. B. Nährwerte 100 g / 1 Sachet) ---------- */
  class LydToggle extends HTMLElement {
    connectedCallback() {
      const btns = this.querySelectorAll('[data-lyd-tg]');
      btns.forEach((b) => b.addEventListener('click', () => {
        btns.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        this.dataset.mode = b.dataset.lydTg;
      }));
    }
  }
  if (!customElements.get('lyd-toggle')) customElements.define('lyd-toggle', LydToggle);

  /* ---------- Weiterlesen (lange Produktbeschreibung) ---------- */
  class LydReadmore extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled !== 'true') return;
      this.body = this.querySelector('[data-lyd-rm-body]');
      this.btn = this.querySelector('[data-lyd-rm-btn]');
      if (!this.body || !this.btn) return;
      this.btn.addEventListener('click', () => this.toggle());
      this.measure = () => {
        const max = window.matchMedia('(min-width: 990px)').matches ? parseInt(this.dataset.max, 10) : parseInt(this.dataset.maxM, 10);
        this.style.setProperty('--rm-max', max + 'px');
        const long = this.body.scrollHeight > max + 80;
        this.btn.hidden = !long;
        if (!long) { this.classList.remove('is-clamped'); return; }
        if (this.btn.getAttribute('aria-expanded') !== 'true') this.classList.add('is-clamped');
      };
      this.measure();
      window.addEventListener('resize', this.measure);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure);
    }
    disconnectedCallback() { window.removeEventListener('resize', this.measure); }
    toggle() {
      const open = this.btn.getAttribute('aria-expanded') !== 'true';
      this.btn.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-clamped', !open);
      this.btn.querySelector('[data-open]').hidden = open;
      this.btn.querySelector('[data-close]').hidden = !open;
      if (!open) this.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'start' });
    }
  }
  if (!customElements.get('lyd-readmore')) customElements.define('lyd-readmore', LydReadmore);

  /* ---------- Kategorie: Filter per AJAX, Drawer, Produkte pro Reihe ---------- */
  class LydFacets extends HTMLElement {
    connectedCallback() {
      this.sid = this.dataset.sectionId;
      this.key = this.dataset.colsKey;
      this.bind();
      this.applyCols();
      window.addEventListener('popstate', () => this.load(window.location.href, false));
    }
    bind() {
      this.addEventListener('change', (e) => {
        const form = e.target.closest('form[data-lyd-ff]') || (e.target.form && e.target.form.matches('[data-lyd-ff]') ? e.target.form : null);
        if (!form) return;
        if (e.target.type === 'number') return;
        this.submit(form);
      });
      this.addEventListener('focusout', (e) => { if (e.target.type === 'number' && e.target.form) this.submit(e.target.form); });
      this.addEventListener('submit', (e) => { const f = e.target.closest('form[data-lyd-ff]'); if (f) { e.preventDefault(); this.submit(f); } });
      this.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-lyd-flink]');
        if (a) { e.preventDefault(); this.load(a.href, true); return; }
        if (e.target.closest('[data-lyd-fopen]')) { this.classList.add('is-open'); document.documentElement.style.overflow = 'hidden'; this.querySelector('[data-lyd-fopen]')?.setAttribute('aria-expanded', 'true'); return; }
        if (e.target.closest('[data-lyd-fclose]')) { this.close(); return; }
        const c = e.target.closest('[data-lyd-cols]'); if (c) { this.store('d', c.dataset.lydCols); this.applyCols(); return; }
        const cm = e.target.closest('[data-lyd-cols-m]'); if (cm) { this.store('m', cm.dataset.lydColsM); this.applyCols(); }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    close() { this.classList.remove('is-open'); document.documentElement.style.overflow = ''; this.querySelector('[data-lyd-fopen]')?.setAttribute('aria-expanded', 'false'); }
    store(k, v) { try { const o = JSON.parse(localStorage.getItem(this.key) || '{}'); o[k] = v; localStorage.setItem(this.key, JSON.stringify(o)); } catch (e) { this.mem = Object.assign(this.mem || {}, { [k]: v }); } }
    read() { try { return JSON.parse(localStorage.getItem(this.key) || '{}'); } catch (e) { return this.mem || {}; } }
    applyCols() {
      const o = this.read(); const d = o.d || this.dataset.cols; const m = o.m || this.dataset.colsM;
      const grid = this.querySelector('[data-lyd-grid]'); if (grid) { grid.style.setProperty('--lyd-cols', d); grid.style.setProperty('--lyd-cols-m', m); }
      this.querySelectorAll('[data-lyd-cols]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.lydCols === String(d))));
      this.querySelectorAll('[data-lyd-cols-m]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.lydColsM === String(m))));
    }
    submit(form) {
      const params = new URLSearchParams(new FormData(form));
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      const url = form.action.split('?')[0] + (params.toString() ? '?' + params.toString() : '');
      this.load(url, true);
    }
    async load(url, push) {
      const wasOpen = this.classList.contains('is-open');
      this.classList.add('is-loading');
      try {
        const u = new URL(url, window.location.origin); u.searchParams.set('section_id', this.sid);
        const html = await fetch(u.toString()).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('lyd-facets');
        if (!fresh) { window.location.href = url; return; }
        this.innerHTML = fresh.innerHTML;
        if (push) { const clean = new URL(url, window.location.origin); clean.searchParams.delete('section_id'); history.pushState({}, '', clean.toString()); }
        this.applyCols();
        if (wasOpen) this.classList.add('is-open');
        this.querySelectorAll('.lyd-rv').forEach((el) => el.classList.add('is-in'));
      } catch (e) { window.location.href = url; }
      finally { this.classList.remove('is-loading'); }
    }
  }
  if (!customElements.get('lyd-facets')) customElements.define('lyd-facets', LydFacets);

  /* ---------- Countdown (Passwortseite) ---------- */
  class LydCountdown extends HTMLElement {
    connectedCallback() {
      const end = Date.parse(this.dataset.end);
      if (isNaN(end)) { this.hidden = true; return; }
      const set = (u, v) => { const el = this.querySelector('[data-u="' + u + '"]'); if (el) el.textContent = String(v).padStart(2, '0'); };
      const tick = () => {
        let d = end - Date.now();
        if (d <= 0) { if (this.dataset.hide === 'true') this.hidden = true; ['d', 'h', 'm', 's'].forEach((u) => set(u, 0)); clearInterval(this.t); return; }
        set('d', Math.floor(d / 864e5)); d %= 864e5; set('h', Math.floor(d / 36e5)); d %= 36e5; set('m', Math.floor(d / 6e4)); d %= 6e4; set('s', Math.floor(d / 1e3));
      };
      tick(); this.t = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.t); }
  }
  if (!customElements.get('lyd-countdown')) customElements.define('lyd-countdown', LydCountdown);
})();

/* EU-Gewährleistung: Fenster öffnen/schließen (Produktbox) */
document.addEventListener('click', (e) => {
  const open = e.target.closest('[data-lyd-gw-open]');
  if (open) { const d = document.getElementById(open.dataset.lydGwOpen); if (d && d.showModal) { d.showModal(); d.querySelector('[data-lyd-gw-close]')?.focus(); } return; }
  const close = e.target.closest('[data-lyd-gw-close]');
  if (close) { close.closest('dialog')?.close(); return; }
  if (e.target.matches && e.target.matches('dialog.lyd-gwd')) e.target.close(); /* Klick auf den Hintergrund */
});

/* Newsletter-Bestätigung (/account/subscribe): Shopify-Standardtext durch leadyourday-Inhalt ersetzen */
(() => {
  const box = document.querySelector('.shopify-email-marketing-confirmation__container');
  if (!box || !/\/account\/subscribe/.test(location.pathname)) return;
  const off = box.querySelector('a[href*="unsubscribe"]');
  const icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  box.innerHTML = '<div class="lyd-sub__icon">' + icon + '</div>'
    + '<p class="lyd-kicker lyd-sub__kicker">willkommen</p>'
    + '<h1>Du bist dabei!</h1>'
    + '<p>Danke fürs Bestätigen. Ab jetzt bekommst du als Erste neue Sorten, Iced-Coffee-Rezepte und Aktionen.</p>'
    + '<p class="lyd-sub__mail">✉&nbsp; Dein 10-%-Code ist unterwegs in dein Postfach</p>'
    + '<p class="lyd-sub__fine">Nichts da? Schau kurz im Spam- oder Werbung-Ordner nach.</p>'
    + '<div class="lyd-sub__cta"><a class="lyd-btn lyd-btn--primary" href="/collections/all">Sorten entdecken</a><a class="lyd-btn lyd-btn--white" href="/">Zur Startseite</a></div>';
  if (off) { off.textContent = 'Newsletter abbestellen'; box.appendChild(off); }
})();
